import type { StoreRole } from '../hooks/useStoreRole'

/**
 * #1439 オーナーが店ごとに決める「誰に何を見せるか」。
 *
 * **見えるか / 見えないかだけ**を対象にする。金が動く操作（返金・予約の承認/キャンセル・
 * 電話予約の登録）とオーナー専用の操作（口座変更・Connect・KYC・退会）は含めない。
 * この線を守ることで、オーナーが何を設定しても #1011 の「乗っ取られても金は
 * 攻撃者に流せない」という前提が店ごとに変わらない。
 *
 * ここは **UX 上の出し分け**であって防御線ではない。実際の拒否は backend の
 * `services/pay/api/services/store_roles.py` の `assert_can_view` が行う。既定値は
 * そちらの `DEFAULT_ROLE_POLICY` と一致させること。
 */
export const VIEWABLE_KEYS = ['reservations', 'sales', 'transactions'] as const
export type ViewableKey = (typeof VIEWABLE_KEYS)[number]

/** rolePolicy を設定できる役割。owner 自身は対象外（常に全部見える）。 */
export const POLICY_ROLES = ['manager', 'staff'] as const
export type PolicyRole = (typeof POLICY_ROLES)[number]

export type RolePolicy = Partial<Record<PolicyRole, Partial<Record<ViewableKey, boolean>>>>

/** 既定値。未設定の店はこれで解決するので backfill 不要（backend と同値）。 */
export const DEFAULT_ROLE_POLICY: Record<PolicyRole, Record<ViewableKey, boolean>> = {
  manager: { reservations: true, sales: true, transactions: true },
  staff: { reservations: true, sales: false, transactions: false },
}

/** `role` が `key` を見られるか。owner は常に true、未知の役割は false（fail-closed）。 */
export function canView(role: StoreRole, key: ViewableKey, policy?: RolePolicy | null): boolean {
  if (role === 'owner') return true
  if (role !== 'manager' && role !== 'staff') return false
  const override = policy?.[role]?.[key]
  return typeof override === 'boolean' ? override : DEFAULT_ROLE_POLICY[role][key]
}
